import { post, get, patch, del, put } from './api-handler.js';
import { withConnectionRefusedRetry } from './transient-retry.js';

const createCategory = payload =>
  withConnectionRefusedRetry(() => post('/budget/api/v1/categories/', payload));

const normalizeReorderItems = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

const toRepositionPayloadItems = items =>
  items.map((item, index) => ({
    id: item?.id ?? item,
    position: item?.position ?? index,
    ...(item?.section !== undefined ? { section: item.section } : {}),
  }));

const toRepositionPayload = (payload, keys, targetKey) => {
  const requestPayload = {
    [targetKey]: toRepositionPayloadItems(normalizeReorderItems(payload, keys)),
  };

  if (!Array.isArray(payload) && Array.isArray(payload?.sections)) {
    requestPayload.sections = payload.sections.map((section, index) => ({
      id: section?.id ?? section,
      position: section?.position ?? index,
    }));
  }

  return requestPayload;
};

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const toMoveCategoryPayload = payload => {
  const requestPayload = {
    category:
      payload?.category ??
      payload?.category_id ??
      payload?.line_item_id ??
      payload?.lineItemId,
    parent: hasOwn(payload, 'parent')
      ? payload.parent
      : payload?.destination_parent_id ??
        payload?.destination?.parent ??
        payload?.destination_swimlane_id ??
        payload?.destination?.swimlane ??
        null,
    position:
      payload?.position ??
      payload?.destination_position ??
      payload?.destination?.position ??
      0,
  };

  if (hasOwn(payload, 'section')) {
    requestPayload.section = payload.section;
  } else if (hasOwn(payload?.destination, 'section')) {
    requestPayload.section = payload.destination.section;
  }

  return requestPayload;
};

const toMoveCategoriesPayload = payload => {
  const requestPayload = {
    categories: payload?.categories || payload?.category_ids || [],
    parent: hasOwn(payload, 'parent')
      ? payload.parent
      : payload?.destination_parent_id ?? payload?.destination?.parent ?? null,
    position:
      payload?.position ??
      payload?.destination_position ??
      payload?.destination?.position ??
      0,
  };

  if (hasOwn(payload, 'section')) {
    requestPayload.section = payload.section;
  } else if (hasOwn(payload?.destination, 'section')) {
    requestPayload.section = payload.destination.section;
  }

  return requestPayload;
};

const API_ENDPOINTS = {
  // ONBOARDING
  onboarding: {
    user_login: payload => post('/api/v1/login/', payload),
    verify_email: payload => post('/api/v1/send-email-otp/', payload),
    user_signup: payload => post('/api/v1/signup/', payload),
    verify_otp: payload => post('/api/v1/verify-email-otp/', payload),
    resend_otp: payload => post('/api/v1/resend-email-otp/', payload),
    forgot_password: payload => post('/api/v1/forgot-password/', payload),
    forgot_password_verify_otp: payload =>
      post('/api/v1/forgot-password-verify-otp/', payload),
    forgot_password_resend_otp: payload =>
      post('/api/v1/resend-forgot-password/', payload),
    social_login: (provider, payload) =>
      post(`/modules/social-auth/${provider}/login/`, payload),
  },
  fringes: {
    create_fringe: payload => post('/fringe/fringe/', payload),
    delete_fringe: payload => del(`/fringe/fringe/delete_multiple/`, payload),
    list_fringe: (filters = {}) => get(`/fringe/fringe/`, { params: filters }),
    update_fringe: payload => put(`/fringe/fringe/${payload?.id}/`, payload),
    list_global_fringe: () => get(`/fringe/fringe/get_global_fringes/`),
    default_fringe: () => get(`/fringe/fringe/default_fringes/`),
    delete_fringe_info: payload =>
      post(`/fringe/fringe/before_delete/`, payload),
    duplicate_fringe: id => post(`/fringe/fringe/${id}/duplicate/`, {}),
  },
  globals: {
    create_global: payload => post('/budget/api/v1/globals/', payload),
    list_globals: (filters = {}) =>
      get('/budget/api/v1/globals/', { params: filters }),
    update_global: payload =>
      patch(`/budget/api/v1/globals/${payload?.id}/`, payload),
    delete_global: id => del(`/budget/api/v1/globals/${id}/`),
    import_to_user: id =>
      post(`/budget/api/v1/globals/${id}/import_to_user/`, {}),
    import_to_budget: ({ id, ...payload }) =>
      post(`/budget/api/v1/globals/${id}/import_to_budget/`, payload),
  },
  ai_suggestions: {
    contextual: payload =>
      post('/budget/api/v1/ai-suggestions/contextual/', payload),
    autocomplete: (payload, config) =>
      post('/budget/api/v1/ai-suggestions/autocomplete/', payload, config),
    insights: (payload, config) =>
      post('/budget/api/v1/ai-suggestions/insights/', payload, config),
    assistantList: (budgetId, config) =>
      get('/budget/api/v1/ai-suggestions/assistant/', {
        ...config,
        params: { budget_id: budgetId },
      }),
    assistantAsk: payload =>
      post('/budget/api/v1/ai-suggestions/assistant/', payload),
    assistantApply: payload =>
      post('/budget/api/v1/ai-suggestions/assistant/apply/', payload),
  },
  tags: {
    create_tag: payload => post('/budget/api/v1/tags/', payload),
    list_tags: (filters = {}) =>
      get('/budget/api/v1/tags/', { params: filters }),
    update_tag: payload =>
      patch(`/budget/api/v1/tags/${payload?.id}/`, payload),
    delete_tag: id => del(`/budget/api/v1/tags/${id}/`),
  },
  top_level_sections: {
    create_section: payload =>
      post('/budget/api/v1/top-level-sections/', payload),
    list_sections: budgetId =>
      get(`/budget/api/v1/top-level-sections/?budget=${budgetId}`),
    update_section: payload =>
      patch(`/budget/api/v1/top-level-sections/${payload?.id}/`, payload),
    delete_section: id => del(`/budget/api/v1/top-level-sections/${id}/`),
  },
  groups: {
    create_group: payload => post('/fringe/groups-fringe/', payload),
    list_group: (filters = {}) =>
      get('/fringe/groups-fringe/', { params: filters }),
    delete_group: payload =>
      del(`/fringe/groups-fringe/delete_multiple/`, payload),
    update_group: payload =>
      put(`/fringe/groups-fringe/${payload?.id}/`, payload),
    add_fringe_to_group: payload =>
      post(`/fringe/groups-fringe/add_fringe/`, payload),
    remove_fringe_from_group: payload =>
      post(`/fringe/groups-fringe/remove_fringe/`, payload),
    delete_group_info: payload =>
      post(`/fringe/groups-fringe/before_delete/`, payload),
    duplicate_group: id => post(`/fringe/groups-fringe/${id}/duplicate/`, {}),
  },
  account_settings: {
    change_password: payload => post('/api/v1/change-password/', payload),
    request_email_change: payload =>
      post('/api/v1/change-email/request/', payload),
    confirm_email_change: payload =>
      post('/api/v1/change-email/confirm/', payload),
    user_logout: payload => post('/api/v1/user-logout/', payload),
    get_user_profile: async () => await get('/users/api/v1/user-profile/'),
    create_user_profile: payload =>
      post('/users/api/v1/user-profile/', payload),
    update_user_profile: ({ setUploadStatus, headers, id, ...payload }) => {
      return patch(`/users/api/v1/user-profile/${id}/`, payload, {
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadStatus &&
            setUploadStatus({
              uploading: true,
              percentCompleted,
            });
        },
        ...(headers || {}),
      });
    },
    search_user: searchTerm =>
      get(`/users/api/v1/search-user/?name=${searchTerm}`),
    get_delete_account_info: () => get('/users/api/v1/delete-account/'),
    authorize_apple_deletion: payload =>
      post('/users/api/v1/delete-account/', payload),
    delete_account: payload => del('/users/api/v1/delete-account/', payload),
  },
  budgets: {
    get_folders: () => get('/budget/api/v1/folder-budget/?folders_only=true'),
    get_folder_by_id: folderId =>
      get(
        `/budget/api/v1/folder-budget/?folders_only=true&parent_folder=${folderId}`
      ),
    move_budget: payload =>
      patch(`/budget/api/v1/create-budget/${payload?.budgetId}/`, payload),
    move_budget_folder: payload =>
      patch(`/budget/api/v1/create-budget/move_budget_folder/`, payload),
    delete_folder: folderId => del(`/budget/api/v1/folders/${folderId}/`),
    create_budget: payload => post('/budget/api/v1/create-budget/', payload),
    analyze_script: (payload, config) =>
      post('/budget/api/v1/script-import/analyze/', payload, config),
    create_budget_from_script: payload =>
      post('/budget/api/v1/script-import/create-budget/', payload),
    create_budget_from_file: (payload, config) =>
      post('/budget/api/v1/budget-import/create-budget/', payload, config),
    create_budget_from_movie_magic: (payload, config) =>
      post('/budget/api/v1/movie-magic-import/create-budget/', payload, config),
    update_budget: payload =>
      patch(`/budget/api/v1/create-budget/${payload?.id}/`, payload),
    create_folder: payload => post('/budget/api/v1/folders/', payload),
    update_folder: payload =>
      patch(`/budget/api/v1/folders/${payload?.id}/`, payload),
    get_budgets: () => get('/budget/api/v1/create-budget/'),
    get_budget_by_id: budgetId =>
      get(`/budget/api/v1/create-budget/${budgetId}/`),
    duplicate_budget: payload =>
      post(`/budget/api/v1/duplicate-budget/`, payload),
    duplicate_budget_values: payload =>
      post(`/budget/api/v1/duplicate-budget-values/`, payload),
    delete_budget: budgetId => del(`/budget/api/v1/create-budget/${budgetId}/`),
    get_budget_types: () => get('/budget/api/v1/budget-type/'),
    //budget swimlanes
    create_swimlane: createCategory,
    delete_swimlane: id => del(`/budget/api/v1/categories/${id}/`),
    create_line_item: createCategory,
    delete_line_item: id => del(`/budget/api/v1/categories/${id}/`),
    create_line_item_children: createCategory,
    delete_line_item_children: id => del(`/budget/api/v1/categories/${id}/`),
    add_sub_category_swimlane: id =>
      post(`/budget/api/v1/categories/${id}/add-sub-category/`),
    add_sub_category_line_item: id =>
      post(`/budget/api/v1/categories/${id}/add-sub-category/`),
    reset_detail_view_swimlane: id =>
      post(`/budget/api/v1/categories/${id}/reset-detail-view/`),
    reset_detail_view_line_item: id =>
      post(`/budget/api/v1/categories/${id}/reset-detail-view/`),
    reset_detail_view_line_item_children: id =>
      post(`/budget/api/v1/categories/${id}/reset-detail-view/`),
    update_swimlane: payload =>
      patch(`/budget/api/v1/categories/${payload.id}/`, payload),
    update_line_item: payload =>
      patch(`/budget/api/v1/categories/${payload.id}/`, payload),
    update_line_item_children: payload =>
      patch(`/budget/api/v1/categories/${payload.id}/`, payload),
    get_swimlanes: budgetId =>
      get(
        `/budget/api/v1/categories/?budget=${budgetId}&parent=root&legacy_shape=1`
      ),
    get_line_items: swimlaneId =>
      get(`/budget/api/v1/categories/?parent=${swimlaneId}&legacy_shape=1`),
    get_line_item_children: parentId =>
      get(`/budget/api/v1/categories/?parent=${parentId}&legacy_shape=1`),
    get_budget_categories: budgetId =>
      get(`/budget/api/v1/categories/?budget=${budgetId}`),
    get_budget_category_tree: budgetId =>
      get(`/budget/api/v1/categories/?budget=${budgetId}&legacy_shape=1`),
    search_budget: ({ budgetId, query, limit = 30 }) =>
      get('/budget/api/v1/categories/search/', {
        params: { budget: budgetId, q: query, limit },
      }),
    get_detail_sheet_totals: (selectedItemId, type) =>
      get(
        `/budget/api/v1/detail-sheet-totals/?selected_item_id=${selectedItemId}&type=${type}`
      ),
    move_detail_row: ({ id, category }) =>
      post(`/budget/api/v1/detail-rows/${id}/move/`, { category }),
    // swimlane details sheet
    get_swimlane_detailed_sheet: async swimlaneId =>
      get(`/budget/api/v1/detail-rows/?category=${swimlaneId}`),
    get_line_item_detailed_sheet: itemId =>
      get(`/budget/api/v1/detail-rows/?category=${itemId}`),
    get_line_item_children_detailed_sheet: itemId =>
      get(`/budget/api/v1/detail-rows/?category=${itemId}`),
    create_swimlane_detail_sheet: (payload, config) =>
      post(`/budget/api/v1/detail-rows/`, payload, config),
    delete_swimlane_details_sheet: (id, config) =>
      del(`/budget/api/v1/detail-rows/${id}/`, undefined, config),
    update_swimlane_detail_sheet: (payload, config) =>
      patch(`/budget/api/v1/detail-rows/${payload.id}/`, payload, config),
    create_line_item_detailed_sheet: (payload, config) =>
      post(`/budget/api/v1/detail-rows/`, payload, config),
    delete_line_item_details_sheet: (id, config) =>
      del(`/budget/api/v1/detail-rows/${id}/`, undefined, config),
    update_line_item_detailed_sheet: (payload, config) =>
      patch(`/budget/api/v1/detail-rows/${payload.id}/`, payload, config),
    create_line_item_children_detailed_sheet: (payload, config) =>
      post(`/budget/api/v1/detail-rows/`, payload, config),
    delete_line_item_children_details_sheet: (id, config) =>
      del(`/budget/api/v1/detail-rows/${id}/`, undefined, config),
    update_line_item_children_detailed_sheet: (payload, config) =>
      patch(`/budget/api/v1/detail-rows/${payload.id}/`, payload, config),
    get_detail_sheet_unit_type: () =>
      get('/budget/api/v1/detail-sheet-unit-type/'),
    // collaborator
    get_permissions: () => get('/budget/api/v1/permissions/'),
    get_collaborators: budgetId =>
      get(
        `/budget/api/v1/registered-invited-collaborators/?budget_id=${budgetId}`
      ),
    get_public_budget_link_settings: budgetId =>
      get(`/budget/api/v1/public-budget-link/${budgetId}/`),
    update_public_budget_link_settings: ({ budgetId, enabled }) =>
      patch(`/budget/api/v1/public-budget-link/${budgetId}/`, { enabled }),
    get_public_budget: token => get(`/budget/api/v1/public-budget/${token}/`),
    share_budget: payload =>
      post('/budget/api/v1/invite-collaborators/', payload),
    remove_budget_access: payload =>
      del(`/budget/api/v1/invite-collaborators/${payload?.id}/`),
    get_budget_permissions: budgetId =>
      get(`/budget/api/v1/budget-permissions/?budget=${budgetId}`),
    get_cell_presence: budgetId =>
      get(`/budget/api/v1/cell-presence/?budget=${budgetId}`),
    update_cell_presence: payload =>
      post('/budget/api/v1/cell-presence/', payload),
    clear_cell_presence: budgetId =>
      del(`/budget/api/v1/cell-presence/clear/?budget=${budgetId}`),
    get_cell_comments: ({ budget, detail_sheet_type, row_id, column_key }) => {
      const params = new URLSearchParams({
        budget: String(budget),
        detail_sheet_type,
        row_id: String(row_id),
        column_key,
      });
      return get(`/budget/api/v1/cell-comments/?${params.toString()}`);
    },
    get_cell_comment_summary: ({ budget, detail_sheet_type }) => {
      const params = new URLSearchParams({
        budget: String(budget),
        detail_sheet_type,
      });
      return get(`/budget/api/v1/cell-comments/summary/?${params.toString()}`);
    },
    get_budget_cell_comment_thread: budget =>
      get(`/budget/api/v1/cell-comments/budget-thread/?budget=${budget}`),
    get_budget_comments: budget =>
      get(`/budget/api/v1/budget-comments/?budget=${budget}`),
    create_cell_comment: payload =>
      post('/budget/api/v1/cell-comments/', payload),
    update_cell_comment: ({ id, body, mentioned_user_ids }) =>
      patch(`/budget/api/v1/cell-comments/${id}/`, {
        body,
        mentioned_user_ids,
      }),
    delete_cell_comment: id => del(`/budget/api/v1/cell-comments/${id}/`),
    create_budget_comment: payload =>
      post('/budget/api/v1/budget-comments/', payload),
    update_budget_comment: ({ id, body, mentioned_user_ids }) =>
      patch(`/budget/api/v1/budget-comments/${id}/`, {
        body,
        mentioned_user_ids,
      }),
    delete_budget_comment: id => del(`/budget/api/v1/budget-comments/${id}/`),
    get_notifications: () => get('/budget/api/v1/notifications/'),
    mark_notification_read: id =>
      post(`/budget/api/v1/notifications/${id}/read/`),
    mark_comment_notifications_read: payload =>
      post('/budget/api/v1/notifications/read-comment/', payload),
    get_change_events: ({ budgetId, since, limit } = {}) => {
      const params = new URLSearchParams();
      if (budgetId) params.append('budget', budgetId);
      if (since) params.append('since', since);
      if (limit) params.append('limit', limit);
      const query = params.toString();
      return get(`/budget/api/v1/change-events/${query ? `?${query}` : ''}`);
    },
    apply_budget_history: payload =>
      post('/budget/api/v1/change-events/apply-history/', payload),
    // get full budget data and download budget
    get_budget_data: budgetId => get(`/budget/api/v1/budget-data/${budgetId}/`),
    get_pdf_options: budgetId =>
      get(
        `/budget/api/v1/pdf-options/?budget_id=${encodeURIComponent(budgetId)}`
      ),
    download_budget_json: payload =>
      post('/budget/api/v1/budget-export/json/', payload, {
        responseType: 'blob',
      }),
    get_budget_details_data: payload => {
      if (payload?.parent_folder) {
        if (payload?.adv_search === true) {
          return get(
            `/budget/api/v1/folder-budget/?advanced_search=${payload?.adv_search}&search=${payload?.search}&parent_folder=${payload?.parent_folder}`
          );
        } else {
          return get(
            `/budget/api/v1/folder-budget/?search=${payload?.search}&parent_folder=${payload?.parent_folder}`
          );
        }
      } else {
        if (payload?.adv_search === true) {
          return get(
            `/budget/api/v1/folder-budget/?advanced_search=${payload?.adv_search}&search=${payload?.search}`
          );
        } else {
          return get(`/budget/api/v1/folder-budget/?search=${payload?.search}`);
        }
      }
    },

    delete_multi_budgets: payload =>
      del(`/budget/api/v1/create-budget/delete_multiple/`, payload),

    download_budget: payload =>
      post(`/budget/api/v1/convert-to-pdf/`, payload, { responseType: 'blob' }),
    get_cost_reports: budgetId =>
      get(`/budget/api/v1/cost-reports/?budget=${budgetId}`),
    preview_cost_report: payload =>
      post('/budget/api/v1/cost-reports/preview/', payload),
    create_cost_report: payload =>
      post('/budget/api/v1/cost-reports/', payload),
    download_cost_report: ({ reportId, format = 'pdf', pageSize = 'letter' }) =>
      get(
        `/budget/api/v1/cost-reports/${reportId}/export/?format=${format}&page_size=${pageSize}`,
        { responseType: 'blob' }
      ),
    // hover_budget: payload =>
    //   post(
    //     `/budget/api/v1/swimlane-detail-sheet/applied_fringe_details/`,
    //     payload
    //   ),

    hover_budget: payload => {
      const { signal, ...rest } = payload; // Extract signal, keep rest of payload
      return post(
        `/budget/api/v1/swimlane-detail-sheet/applied_fringe_details/`,
        rest,
        { signal }
      );
    },
    // Contractual Charges
    get_contractual_charges: budgetId =>
      get(`/budget/api/v1/contractual-charges/?budget=${budgetId}`),
    create_contractual_charge: payload =>
      post('/budget/api/v1/contractual-charges/', payload),
    update_contractual_charge: payload =>
      patch(`/budget/api/v1/contractual-charges/${payload.id}/`, payload),
    delete_contractual_charge: id =>
      del(`/budget/api/v1/contractual-charges/${id}/`),
    reposition_contractual_charges: payload =>
      post('/budget/api/v1/reposition-contractual-charges/', payload),
    // Film Tax Credit Estimates
    get_film_incentives: budgetId =>
      get(`/budget/api/v1/film-incentives/?budget=${budgetId}`),
    create_film_incentive: payload =>
      post('/budget/api/v1/film-incentives/', payload),
    update_film_incentive: payload =>
      patch(`/budget/api/v1/film-incentives/${payload.id}/`, payload),
    delete_film_incentive: id => del(`/budget/api/v1/film-incentives/${id}/`),
  },
  linked_fringes: {
    link_fringes: payload => post('/budget/api/v1/linked-fringes/', payload),
    unlink_fringes: payload => del('/budget/api/v1/linked-fringes/', payload),
    get_linked_fringes_by_group: filters =>
      get(`/budget/api/v1/linked-fringes/`, { params: filters }),
    unlink: payload => del(`/budget/api/v1/linked-fringes/unlink/`, payload),
  },
  tabs: {
    get_open_tabs: () => get('/budget/api/v1/open-tabs/'),
    make_active_tab: tabId =>
      post(`/budget/api/v1/open-tabs/${tabId}/make-active/`, {}),
    get_active_budget_users: budgetId =>
      get(`/budget/api/v1/open-tabs/active-users/?budget=${budgetId}`),
    create_tabs: payload => post('/budget/api/v1/open-tabs/', payload),
    reorder_tabs: payload => post('/budget/api/v1/open-tabs/reorder/', payload),
    update_view_state: ({ tabId, view_state }) =>
      patch(`/budget/api/v1/open-tabs/${tabId}/`, { view_state }),
    close_tab: tabId => del(`/budget/api/v1/open-tabs/${tabId}/`),
  },
  contextual_suggestion: {
    get_contextual_suggestions: () =>
      get('/contextual-suggestion/api/v1/contextual-suggestions/'),
  },
  user_activity: {
    post_user_activity: payload =>
      post('/users/api/v1/user-activity/', payload),
  },
  reorder_swimlane: {
    reorder_swimlane: payload =>
      post(
        '/budget/api/v1/reposition-categories/',
        toRepositionPayload(payload, ['categories', 'swimlanes'], 'categories')
      ),
  },
  reorder_line_item: {
    reorder_line_item: payload =>
      post(
        '/budget/api/v1/reposition-categories/',
        toRepositionPayload(
          payload,
          [
            'categories',
            'line_items',
            'source_line_items',
            'destination_line_items',
          ],
          'categories'
        )
      ),
  },
  move_line_item_category: {
    move_line_item_category: payload =>
      post('/budget/api/v1/move-category/', toMoveCategoryPayload(payload)),
    move_line_item_categories: payload =>
      post('/budget/api/v1/move-categories/', toMoveCategoriesPayload(payload)),
  },
  reorder_detail_sheet: {
    reorder_detail_sheet: payload =>
      post(
        '/budget/api/v1/reposition-detail-rows/',
        toRepositionPayload(
          payload,
          ['detail_rows', 'detail_sheet_items', 'reorder_data'],
          'detail_rows'
        )
      ),
  },
  reorder_line_item_detail_sheet: {
    reorder_line_item_detail_sheet: payload =>
      post(
        '/budget/api/v1/reposition-detail-rows/',
        toRepositionPayload(
          payload,
          ['detail_rows', 'detail_sheet_items', 'reorder_data'],
          'detail_rows'
        )
      ),
  },
  duplicate_category: {
    duplicate_category: payload =>
      post(
        `/budget/api/v1/categories/${
          payload?.category_id ?? payload?.swimlane_id ?? payload?.id
        }/duplicate/`,
        {}
      ),
  },
  duplicate_category_line_item: {
    duplicate_category_line_item: payload =>
      post(
        `/budget/api/v1/categories/${
          payload?.category_id ?? payload?.line_item_id ?? payload?.id
        }/duplicate/`,
        {}
      ),
  },
};
export default API_ENDPOINTS;
